import React, {
  memo,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@apollo/client';
import { LexicalEditor as LexicalEditorType } from 'lexical';
import { up } from 'styled-breakpoints';
import styled from 'styled-components';
import {
  Mutation,
  ContactInteractionChannelAvailabilityForContactResourceToContactAddressAvailability,
  MutationConversationSendWhatsappMessageArgs,
  WhatsappTemplate,
  ContactInteractionChannelWhatsappResource,
} from '@lgg/isomorphic/types/__generated__/graphql';
import { Scrollbar } from 'src/components/general/display/scrollbar';
import { DrawerSelectableOption } from 'src/components/general/drawer/bottom/selectable-options-bottom-drawer';
import { Icon } from 'src/components/general/icon';
import { FlexColumn } from 'src/components/layout/flex-column';
import {
  ActiveAiAgentBanner,
  useHasActiveAiAgent,
} from 'src/components/pages/conversations/components/contact-interactions/contact-interactions-input-area/active-ai-agent-banner';
import {
  WhatsappBannedResourceBanner,
  WhatsappMessageClosedWindowContent,
  WhatsappResourceInactiveBanner,
  WhatsappResourceReviewPendingBanner,
} from 'src/components/pages/conversations/components/contact-interactions/contact-interactions-input-area/channels/whatsapp/whatsapp';
import {
  AddWhatsappTemplateOption,
  SendTemplateOptionIcon,
} from 'src/components/pages/conversations/components/contact-interactions/contact-interactions-input-area/channels/whatsapp/whatsapp-template/add-whatsapp-template-option';
import {
  templateParametersDefaultValue,
  useSendWhatsappTemplate,
} from 'src/components/pages/conversations/components/contact-interactions/contact-interactions-input-area/channels/whatsapp/whatsapp-template/use-send-whatsapp-template';
import { ContactInteractionInputAreaCharacterCounter } from 'src/components/pages/conversations/components/contact-interactions/contact-interactions-input-area/contact-interaction-input-area-character-counter';
import { ContactInteractionInputManagerContext } from 'src/components/pages/conversations/components/contact-interactions/contact-interactions-input-area/contact-interaction-input-manager';
import { CustomEditor } from 'src/components/pages/conversations/components/contact-interactions/contact-interactions-input-area/editors/custom-editor';
import {
  CustomEditorProps,
  getContactAddressOptionTestId,
  mapChannelResourcesToOptions,
  useHandleReplyError,
  useMapContactAddressesToOptions,
} from 'src/components/pages/conversations/components/contact-interactions/contact-interactions-input-area/editors/helpers';
import { CONVERSATION_SEND_WHATSAPP_MESSAGE } from 'src/components/pages/conversations/components/contact-interactions/contact-interactions-input-area/graphql-operations';
import { ReplyWindowBar } from 'src/components/pages/conversations/components/contact-interactions/contact-interactions-input-area/reply-window-bar';
import { ResourceSelectorBar } from 'src/components/pages/conversations/components/contact-interactions/contact-interactions-input-area/resource-selector-bar';
import { AddContactInteractionModal } from 'src/components/pages/conversations/components/contact-interactions/dev-tools/add-contact-interaction-modal';
import { WhatsappTemplatePreviewItem } from 'src/components/pages/conversations/components/contact-interactions/items/whatsapp/whatsapp-subitems/whatsapp-template-item';
import { useAuthorization } from 'src/hooks/use-authorization';

const TopContentContainer = styled(FlexColumn)`
  max-height: calc(100% - 23px);

  ${up('md')} {
    max-height: calc(100% - 28px);
  }
`;

const EditTemplateContainer = styled.div`
  height: max-content;
  position: relative;

  ${up('md')} {
    max-height: 400px;
  }
`;

const EditTemplateScrollbar = styled(Scrollbar)`
  padding: 15px 20px 0px;
  margin-bottom: 15px;
  max-height: 100%;
`;

const EditTemplateCancelIcon = styled(Icon)`
  cursor: pointer;
  position: absolute;
  right: 15px;
  top: 11px;
  z-index: 10;

  svg {
    height: 14px;
    width: 14px;

    path {
      fill: ${({ theme }) => theme.colors.flint};
    }
  }
`;

type WhatsappEditorProps = CustomEditorProps & {
  resources: ContactInteractionChannelWhatsappResource[];
};

export const WhatsappEditor = memo<WhatsappEditorProps>(
  ({
    handleFetchNewMessageCompleted,
    channelAvailability,
    availabilityMessageRef,
    resources,
  }) => {
    const {
      messageValues,
      attachmentList,
      conversationId,
      hasFocus,
      contactPhoneNumbers,
    } = useContext(ContactInteractionInputManagerContext);

    const [selectedResource, setSelectedResource] =
      useState<ContactInteractionChannelWhatsappResource | null>(null);
    const [selectedContactAddress, setSelectedContactAddress] = useState<string | null>(
      null,
    );
    const contactAddressOptions = useMemo<DrawerSelectableOption[]>(
      () =>
        channelAvailability?.contactIdentifiers?.map(({ value, label }) => ({
          label,
          value,
          onClick: () => setSelectedContactAddress(value),
          'data-lgg-id': getContactAddressOptionTestId(value),
        })) ?? [],
      [channelAvailability?.contactIdentifiers],
    );
    const [selectedTemplate, setSelectedTemplate] = useState<
      WhatsappTemplate | undefined
    >(undefined);
    const [selectedResourceAvailability, setSelectedResourceAvailability] = useState<
      | ContactInteractionChannelAvailabilityForContactResourceToContactAddressAvailability
      | undefined
    >(undefined);
    const { mapContactAddressToOption } = useMapContactAddressesToOptions();
    const { t } = useTranslation(['errors', 'conversations', 'common', 'smsChannel']);
    const handleReplyError = useHandleReplyError();
    const hasActiveAiAgent = useHasActiveAiAgent();
    const { getFeatureFlag } = useAuthorization();
    const isAiToolsFeatureEnabled = getFeatureFlag('AI');
    const isMessageTemplatesFeatureEnabled = getFeatureFlag(
      'contact-interaction-templates',
    );
    const {
      isSendingWhatsappTemplate,
      handleSendTemplate,
      templateParameters,
      setTemplateParameters,
    } = useSendWhatsappTemplate({
      conversationId,
      onCompleted: (newInteractionId) => {
        setSelectedTemplate(undefined);

        void handleFetchNewMessageCompleted({
          where: {
            contactInteractionId: {
              eq: newInteractionId,
            },
          },
        });
      },
      contactPhoneNumber: selectedContactAddress ?? undefined,
      contactAddress: selectedContactAddress ?? undefined,
      selectedResource,
      selectedTemplate,
    });

    const isEditingTemplate = useMemo(() => !!selectedTemplate, [selectedTemplate]);

    useEffect(() => {
      if (!channelAvailability) {
        return;
      }

      const { lastUsedResourceId, lastInteractionContactAddress } = channelAvailability;

      const selectedResourceAvailability =
        channelAvailability.resourceToContactAddressAvailability?.find(
          (resource) =>
            resource.resourceId === selectedResource?.id &&
            resource.contactAddress === selectedContactAddress,
        );

      setSelectedResourceAvailability(selectedResourceAvailability);

      if (!selectedResource) {
        const defaultSelectedResource =
          resources.find(({ id }) => id === lastUsedResourceId) ?? resources[0] ?? null;

        setSelectedResource(defaultSelectedResource);
      } else {
        const updatedSelectedResource = resources.find(
          ({ id }) => id === selectedResource?.id,
        );

        setSelectedResource(updatedSelectedResource ?? null);
      }

      if (!selectedContactAddress) {
        const defaultSelectedContactAddress =
          channelAvailability.contactIdentifiers?.find(
            ({ value }) => value === lastInteractionContactAddress,
          )?.value ??
          channelAvailability.contactIdentifiers?.[0]?.value ??
          null;

        setSelectedContactAddress(defaultSelectedContactAddress);
      }
    }, [
      channelAvailability,
      selectedResource?.id,
      selectedContactAddress,
      selectedResource,
      resources,
    ]);

    useEffect(() => {
      if (selectedResource) {
        setSelectedTemplate(undefined);
      }
    }, [selectedResource]);

    const isContactWindowOpen = useMemo(
      () =>
        !!channelAvailability?.isAvailable &&
        !!selectedResourceAvailability &&
        selectedResourceAvailability.unavailableReasonCode !== 'CHAT_WINDOW_CLOSED',
      [channelAvailability?.isAvailable, selectedResourceAvailability],
    );

    const [sendWhatsappMessage, { loading: isSendingWhatsappMessage }] = useMutation<
      Pick<Mutation, 'conversationSendWhatsappMessage'>,
      MutationConversationSendWhatsappMessageArgs
    >(CONVERSATION_SEND_WHATSAPP_MESSAGE, {
      onError: handleReplyError,
    });

    const handleSendMessage = useCallback(
      async (editor: LexicalEditorType) => {
        if (
          !conversationId ||
          !selectedResourceAvailability ||
          isSendingWhatsappMessage
        ) {
          return;
        }

        let commonVariables: Pick<
          MutationConversationSendWhatsappMessageArgs,
          'conversationId' | 'message' | 'attachments'
        > = {
          conversationId,
          message: messageValues.message ?? '',
          attachments: [],
        };

        const attachments = attachmentList.map(({ url }) => ({
          s3UploadedUrl: url ?? '',
        }));

        if (attachments.length > 0) {
          commonVariables = {
            ...commonVariables,
            attachments,
          };
        }

        if (!selectedResourceAvailability) {
          return;
        }

        await sendWhatsappMessage({
          variables: {
            ...commonVariables,
            contactAddress:
              selectedContactAddress ?? selectedResourceAvailability.contactAddress,
            contactPhoneNumber:
              selectedContactAddress ?? selectedResourceAvailability.contactAddress,
            resourceId: selectedResourceAvailability.resourceId,
          },
          onCompleted: async ({ conversationSendWhatsappMessage: data }) => {
            await handleFetchNewMessageCompleted({
              editor,
              where: {
                contactInteractionId: {
                  eq: Number(data.interaction.id),
                },
              },
            });
          },
        });
      },
      [
        isSendingWhatsappMessage,
        selectedResourceAvailability,
        conversationId,
        messageValues.message,
        attachmentList,
        sendWhatsappMessage,
        selectedContactAddress,
        handleFetchNewMessageCompleted,
      ],
    );

    const onTemplateSelect = useCallback(
      (template?: WhatsappTemplate) => {
        setTemplateParameters(templateParametersDefaultValue);
        setSelectedTemplate(template);
      },
      [setTemplateParameters],
    );

    const isInputEnabled = useMemo(
      () => !hasActiveAiAgent && (isContactWindowOpen || isEditingTemplate),
      [hasActiveAiAgent, isContactWindowOpen, isEditingTemplate],
    );

    const showWhatsappTemplateOption = useMemo(
      () => selectedResource,
      [selectedResource],
    );

    const getContactPhoneSelectorBar = useCallback(() => {
      const selectedContactAddressOption = contactAddressOptions.find(
        ({ value }) => value === selectedContactAddress,
      );

      return selectedContactAddressOption && contactAddressOptions.length > 0 ? (
        <ResourceSelectorBar
          label={t('common:to')}
          selectedResource={selectedContactAddressOption}
          options={contactAddressOptions.map((option) => {
            const contactPhoneNumber = contactPhoneNumbers.find((phoneOption) => {
              return phoneOption.e164 === option.value;
            });

            if (contactPhoneNumber) {
              return mapContactAddressToOption({
                phoneOption: contactPhoneNumber,
                onClick: () => setSelectedContactAddress(option.value),
              });
            }

            return {
              ...option,
              'data-lgg-id': getContactAddressOptionTestId(option.value),
            };
          })}
          data-lgg-id="contact-phone-selector-dropdown-button"
        />
      ) : null;
    }, [
      contactAddressOptions,
      contactPhoneNumbers,
      mapContactAddressToOption,
      selectedContactAddress,
      t,
    ]);

    const getPhoneResourceSelectorBar = useCallback(
      ({ testIdPrefix }: { testIdPrefix: string }) => {
        return selectedResource && resources.length > 0 ? (
          <ResourceSelectorBar
            label={t('common:from')}
            selectedResource={selectedResource}
            options={mapChannelResourcesToOptions({
              resources,
              testIdGenerator: (id) => `${testIdPrefix}-option-${id}`,
              onClick: (resource) => {
                setSelectedResource(
                  resource as ContactInteractionChannelWhatsappResource,
                );
              },
            })}
            data-lgg-id={`${testIdPrefix}-selector-dropdown-button`}
          />
        ) : null;
      },
      [resources, selectedResource, t],
    );

    const unavailableResourceBanner = useMemo(() => {
      if (hasActiveAiAgent) {
        return <ActiveAiAgentBanner />;
      }

      const isWhatsappResourceBanned =
        channelAvailability &&
        selectedResource?.unavailableReasonCode === 'WHATSAPP_WABA_BANNED';

      const isWhatsappResourceReviewPending =
        channelAvailability &&
        selectedResource?.unavailableReasonCode === 'WHATSAPP_WABA_REVIEW_PENDING';

      const isWhatsappResourceInactive =
        channelAvailability && !selectedResource?.isActive;

      const isResourceAvailable =
        isContactWindowOpen &&
        !isWhatsappResourceBanned &&
        !isWhatsappResourceReviewPending &&
        !isWhatsappResourceInactive;

      if (isResourceAvailable) {
        return null;
      }

      if (isWhatsappResourceBanned && selectedResource) {
        return (
          <WhatsappBannedResourceBanner
            waba={selectedResource.waba}
            violation={selectedResource?.bannedState?.violation}
            date={selectedResource?.bannedState?.date}
          />
        );
      }

      if (isWhatsappResourceReviewPending) {
        return <WhatsappResourceReviewPendingBanner />;
      }

      if (!isWhatsappResourceInactive && !isContactWindowOpen && selectedResource) {
        return (
          <WhatsappMessageClosedWindowContent
            resourceId={selectedResource?.id}
            isWindowOpen={isContactWindowOpen}
            onTemplateSelect={onTemplateSelect}
          />
        );
      }

      if (isWhatsappResourceInactive && selectedResource) {
        return <WhatsappResourceInactiveBanner waba={selectedResource.waba} />;
      }

      return null;
    }, [
      channelAvailability,
      hasActiveAiAgent,
      isContactWindowOpen,
      onTemplateSelect,
      selectedResource,
    ]);

    const topContent = useMemo(() => {
      if (!selectedResource || !selectedContactAddress || !conversationId) {
        return null;
      }

      const showResourceSelector =
        !hasActiveAiAgent && (hasFocus || !isContactWindowOpen);

      return (
        <TopContentContainer>
          {showResourceSelector ? (
            <>
              {getPhoneResourceSelectorBar({
                testIdPrefix: 'whatsapp-resource',
              })}
              {getContactPhoneSelectorBar()}
            </>
          ) : null}
          {selectedTemplate ? (
            <EditTemplateScrollbar>
              <EditTemplateContainer data-lgg-id="edit-whatsapp-template-container">
                <EditTemplateCancelIcon
                  type="close"
                  lggTestId="edit-whatsapp-template-cancel-icon"
                  onClick={() => {
                    setSelectedTemplate(undefined);
                  }}
                />
                <WhatsappTemplatePreviewItem
                  template={selectedTemplate}
                  parameters={templateParameters}
                  setParameters={setTemplateParameters}
                />
              </EditTemplateContainer>
            </EditTemplateScrollbar>
          ) : (
            unavailableResourceBanner
          )}
        </TopContentContainer>
      );
    }, [
      conversationId,
      getContactPhoneSelectorBar,
      getPhoneResourceSelectorBar,
      hasFocus,
      hasActiveAiAgent,
      isContactWindowOpen,
      selectedContactAddress,
      selectedResource,
      selectedTemplate,
      setTemplateParameters,
      templateParameters,
      unavailableResourceBanner,
    ]);

    const replyWindowBar = useMemo(() => {
      return isContactWindowOpen && availabilityMessageRef?.current
        ? createPortal(
            <ReplyWindowBar
              expirationDate={selectedResourceAvailability?.expiresAt}
              type="WHATSAPP"
            />,
            availabilityMessageRef.current,
          )
        : null;
    }, [
      availabilityMessageRef,
      isContactWindowOpen,
      selectedResourceAvailability?.expiresAt,
    ]);

    const extraOptions = useMemo(() => {
      if (!conversationId) {
        return null;
      }

      const showTemplateOption =
        !hasActiveAiAgent &&
        showWhatsappTemplateOption &&
        selectedResource &&
        selectedContactAddress;

      return (
        <>
          {showTemplateOption ? (
            <AddWhatsappTemplateOption
              resourceData={{
                resourceId: selectedResource.id,
              }}
              renderTrigger={({ visible, showModal }) => (
                <SendTemplateOptionIcon visible={visible} showModal={showModal} />
              )}
              onTemplateSelect={(template) => {
                onTemplateSelect(template);
              }}
            />
          ) : null}
          {getFeatureFlag('testing_toolkit') ? (
            <AddContactInteractionModal
              channelAvailability={channelAvailability}
              conversationId={conversationId}
            />
          ) : null}
        </>
      );
    }, [
      channelAvailability,
      conversationId,
      getFeatureFlag,
      hasActiveAiAgent,
      onTemplateSelect,
      selectedContactAddress,
      selectedResource,
      showWhatsappTemplateOption,
    ]);

    return (
      <>
        {replyWindowBar}
        <CustomEditor
          handleSubmission={async (editor) => {
            if (isEditingTemplate) {
              await handleSendTemplate();
            } else {
              await handleSendMessage(editor);
            }
          }}
          isSubmitting={
            isEditingTemplate ? isSendingWhatsappTemplate : isSendingWhatsappMessage
          }
          bottomContent={{
            leading: extraOptions,
            visible: true,
          }}
          topContent={topContent}
          pluginConfig={{
            emojiPicker: {
              visible: true,
              disabled: isEditingTemplate,
            },
            messageAttachments: {
              visible: true,
              disabled: isEditingTemplate,
            },
            aiTools: {
              visible: isAiToolsFeatureEnabled,
              disabled: isEditingTemplate,
            },
            messageTemplates: {
              visible: isMessageTemplatesFeatureEnabled,
              disabled: isEditingTemplate,
            },
            inv360: {
              visible: true,
              disabled: isEditingTemplate,
            },
          }}
          isInputEnabled={isInputEnabled}
          forceFocus={isEditingTemplate}
          forceEnableSendButton={isEditingTemplate}
          isLoading={isSendingWhatsappTemplate}
          showInput={!isEditingTemplate}
          rightRailLeadingContent={
            !isEditingTemplate ? <ContactInteractionInputAreaCharacterCounter /> : null
          }
        />
      </>
    );
  },
);
